import { Download, Visibility } from "@mui/icons-material";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from "@mui/material";
import React from "react";
import * as XLSX from "xlsx";
import { ImportItem, ImportStatus } from "../../types";
import ImportDetailsDialog from "../ImportDetailsDialog";

type Props = {
  items: ImportItem[];
};

type SortKey =
  | "source"
  | "device"
  | "patient"
  | "result"
  | "answers"
  | "status";

type SortDirection = "asc" | "desc";

const statusConfig: Record<
  ImportStatus,
  { label: string; color: "default" | "info" | "success" | "warning" | "error" }
> = {
  INVALID: { label: "Inválido", color: "error" },
  PENDING_VALIDATION: { label: "Aguardando validação", color: "default" },
  READY: { label: "Pronto para sincronizar", color: "info" },
  ALREADY_SYNCED: { label: "Já estava sincronizado", color: "success" },
  IMPORTING: { label: "Sincronizando", color: "info" },
  IMPORTED: { label: "Sincronizado agora", color: "success" },
  PARTIAL: { label: "Sincronização parcial", color: "warning" },
  FAILED: { label: "Falhou", color: "error" }
};

const terminalStatuses: ImportStatus[] = [
  "INVALID",
  "ALREADY_SYNCED",
  "IMPORTED",
  "PARTIAL",
  "FAILED"
];

const statusPriority: Record<ImportStatus, number> = {
  PENDING_VALIDATION: 0,
  READY: 0,
  IMPORTING: 0,
  PARTIAL: 0,
  FAILED: 0,
  INVALID: 1,
  ALREADY_SYNCED: 2,
  IMPORTED: 2
};

const textCollator = new Intl.Collator("pt-BR", {
  numeric: true,
  sensitivity: "base"
});

const sortValue = (item: ImportItem, sortKey: SortKey): string | number => {
  switch (sortKey) {
    case "source":
      return `${item.sourceArchive} ${item.sourceFile}`;
    case "device":
      return item.device;
    case "patient":
      return item.data?.account?.name ?? "";
    case "result":
      return item.data?.testeResult?.testResultId ?? "";
    case "answers":
      return item.data?.testeResult?.quizAnswer?.length ?? 0;
    case "status":
      return statusPriority[item.status];
  }
};

const compareValues = (left: string | number, right: string | number) => {
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }

  return textCollator.compare(String(left), String(right));
};

const getDetails = (item: ImportItem) =>
  item.message || [...item.errors, ...item.warnings].join("; ") || "Sem observações";

const ImportReport: React.FC<Props> = ({ items }) => {
  const [selectedItemId, setSelectedItemId] = React.useState<string | null>(null);
  const [sortKey, setSortKey] = React.useState<SortKey>("status");
  const [sortDirection, setSortDirection] = React.useState<SortDirection>("asc");

  const sortedItems = React.useMemo(
    () =>
      items
        .map((item, originalIndex) => ({ item, originalIndex }))
        .sort((left, right) => {
          const comparison = compareValues(
            sortValue(left.item, sortKey),
            sortValue(right.item, sortKey)
          );

          if (comparison !== 0) {
            return sortDirection === "asc" ? comparison : -comparison;
          }

          return left.originalIndex - right.originalIndex;
        })
        .map(({ item }) => item),
    [items, sortDirection, sortKey]
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(current => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortKey(key);
    setSortDirection("asc");
  };

  const sortableHeader = (key: SortKey, label: string) => (
    <TableSortLabel
      active={sortKey === key}
      direction={sortKey === key ? sortDirection : "asc"}
      onClick={() => handleSort(key)}
    >
      {label}
    </TableSortLabel>
  );

  if (!items.length) return null;

  const counts = items.reduce<Record<string, number>>((result, item) => {
    result[item.status] = (result[item.status] ?? 0) + 1;
    return result;
  }, {});
  const canExport = items.every(item => terminalStatuses.includes(item.status));
  const selectedItem = items.find(item => item.id === selectedItemId) ?? null;

  const exportReport = () => {
    const rows = items.map(item => ({
      "Pacote de origem": item.sourceArchive,
      "Arquivo JSON": item.sourceFile,
      "Dispositivo / MAC": item.device,
      Paciente: item.data?.account?.name ?? "Não identificado",
      "E-mail": item.data?.account?.email ?? "",
      "ID do resultado": item.data?.testeResult?.testResultId ?? "",
      "Total de respostas": item.data?.testeResult?.quizAnswer?.length ?? 0,
      Situação: statusConfig[item.status].label,
      "Conta já existia": item.accountExists === undefined ? "" : item.accountExists ? "Sim" : "Não",
      "Resultado já existia": item.resultExists === undefined ? "" : item.resultExists ? "Sim" : "Não",
      Detalhes: getDetails(item),
      "Processado em": item.processedAt
        ? new Date(item.processedAt).toLocaleString("pt-BR")
        : ""
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet["!cols"] = [
      { wch: 28 },
      { wch: 42 },
      { wch: 20 },
      { wch: 28 },
      { wch: 30 },
      { wch: 38 },
      { wch: 20 },
      { wch: 25 },
      { wch: 18 },
      { wch: 21 },
      { wch: 60 },
      { wch: 22 }
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Relatório de importação");
    XLSX.writeFile(
      workbook,
      `relatorio-importacao-${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title="Auditoria da sincronização"
        subheader={`${items.length} resultado(s) encontrado(s) nos arquivos selecionados`}
        action={
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={exportReport}
            disabled={!canExport}
          >
            Exportar relatório
          </Button>
        }
      />
      <CardContent>
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          {(Object.keys(statusConfig) as ImportStatus[])
            .filter(status => counts[status])
            .map(status => (
              <Chip
                key={status}
                label={`${statusConfig[status].label}: ${counts[status]}`}
                color={statusConfig[status].color}
                variant={status === "INVALID" || status === "FAILED" ? "filled" : "outlined"}
              />
            ))}
        </Stack>

        {counts.PARTIAL > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Existem contas criadas cujos resultados não foram sincronizados. Consulte os detalhes antes de repetir a operação.
          </Alert>
        )}

        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>{sortableHeader("source", "Origem")}</TableCell>
                <TableCell>{sortableHeader("device", "Dispositivo")}</TableCell>
                <TableCell>{sortableHeader("patient", "Paciente")}</TableCell>
                <TableCell>{sortableHeader("result", "Resultado")}</TableCell>
                <TableCell align="center">
                  {sortableHeader("answers", "Respostas")}
                </TableCell>
                <TableCell>{sortableHeader("status", "Situação")}</TableCell>
                <TableCell>Detalhes</TableCell>
                <TableCell align="right">Conferir</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedItems.map(item => (
                <TableRow
                  key={item.id}
                  hover
                  onClick={() => setSelectedItemId(item.id)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Typography variant="body2">{item.sourceArchive}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.sourceFile}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.device}</TableCell>
                  <TableCell>{item.data?.account?.name ?? "—"}</TableCell>
                  <TableCell>{item.data?.testeResult?.testResultId ?? "—"}</TableCell>
                  <TableCell align="center">
                    {item.data?.testeResult?.quizAnswer?.length ?? 0}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={statusConfig[item.status].label}
                      color={statusConfig[item.status].color}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 240 }}>{getDetails(item)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label={`Conferir ${item.data?.account?.name ?? "item"}`}
                      onClick={event => {
                        event.stopPropagation();
                        setSelectedItemId(item.id);
                      }}
                    >
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
      <ImportDetailsDialog
        item={selectedItem}
        onClose={() => setSelectedItemId(null)}
      />
    </Card>
  );
};

export default ImportReport;
